import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Shield, CreditCard, CheckCircle, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { WhatsAppPostPurchaseButton } from '../components/WhatsAppButton';

export default function ProtectedPaymentsPage() {
  const { user, isSeller } = useAuth();
  const [searchParams] = useSearchParams();
  const [connectStatus, setConnectStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (searchParams.get('connected') === 'true') {
      toast.success('Cuenta de pagos conectada exitosamente');
      loadData();
    }
    if (searchParams.get('refresh') === 'true') {
      loadData();
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const status = await api.getConnectStatus();
      setConnectStatus(status);
      if (isSeller) {
        const s = await api.getMySales();
        setSales(s);
      }
      const p = await api.getMyPurchases();
      setPurchases(p);
    } catch (e) {
      // Not connected yet
      setConnectStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  const handleOnboard = async () => {
    setOnboarding(true);
    try {
      const result = await api.startConnectOnboarding(window.location.origin);
      if (result.status === 'already_connected') {
        toast.info('Tu cuenta ya está conectada');
        loadData();
      } else if (result.url) {
        window.location.href = result.url;
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOnboarding(false);
    }
  };

  const handleDashboard = async () => {
    try {
      const result = await api.getConnectDashboard();
      window.open(result.url, '_blank');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleShip = async (txId) => {
    try {
      await api.markShipped(txId);
      toast.success('Envío confirmado');
      loadData();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleConfirmDelivery = async (txId) => {
    try {
      await api.confirmDelivery(txId);
      toast.success('Recepción confirmada. Fondos liberados al vendedor.');
      loadData();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const formatPrice = (p) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(p);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const STATUS_LABELS = {
    pending: { label: 'Pendiente', color: 'text-zinc-400 bg-zinc-500/10' },
    paid: { label: 'Pagado - En custodia', color: 'text-blue-300 bg-blue-500/10' },
    shipped: { label: 'Enviado', color: 'text-amber-300 bg-amber-500/10' },
    delivered: { label: 'Entregado', color: 'text-emerald-300 bg-emerald-500/10' },
    released: { label: 'Fondos liberados', color: 'text-emerald-300 bg-emerald-500/10' },
    auto_released: { label: 'Auto-liberado (48h)', color: 'text-emerald-300 bg-emerald-500/10' },
    disputed: { label: 'En disputa', color: 'text-red-300 bg-red-500/10' },
    resolved: { label: 'Resuelto', color: 'text-zinc-300 bg-zinc-500/10' },
    refunded: { label: 'Reembolsado', color: 'text-red-300 bg-red-500/10' },
    canceled: { label: 'Cancelado', color: 'text-zinc-400 bg-zinc-500/10' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="protected-payments-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
          <Shield className="w-5 h-5 text-purple-400" />
        </div>
        <div>
          <h1 className="text-2xl font-medium text-zinc-100 font-[Outfit]">Compra Protegida</h1>
          <p className="text-sm text-zinc-500">Transacciones seguras con custodia de fondos</p>
        </div>
      </div>

      {/* Connect Status — Seller */}
      {isSeller && (
        <div className="glass-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-zinc-100 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-purple-400" />
              Cuenta de pagos
            </h2>
            {connectStatus?.charges_enabled && (
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                Activa
              </span>
            )}
          </div>

          {!connectStatus?.connected ? (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">
                Conecta tu cuenta bancaria para recibir pagos a través de Compra Protegida.
                Los fondos se liberan al confirmar la recepción del comprador o automáticamente en 48 horas.
              </p>
              <div className="p-4 bg-purple-500/5 border border-purple-500/10 rounded-xl space-y-2">
                <p className="text-sm text-purple-300 font-medium">Beneficios de Compra Protegida:</p>
                <ul className="text-xs text-zinc-400 space-y-1">
                  <li>&#8226; Pagos seguros con custodia de fondos</li>
                  <li>&#8226; Auto-liberación a 48h sin disputa</li>
                  <li>&#8226; Badge "Vendedor Protegido" en tus anuncios</li>
                  <li>&#8226; Mayor confianza = más ventas</li>
                </ul>
              </div>
              <Button
                onClick={handleOnboard}
                disabled={onboarding}
                className="bg-purple-600 hover:bg-purple-500 text-white"
                data-testid="connect-onboard-btn"
              >
                {onboarding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                {onboarding ? 'Redirigiendo a Stripe...' : 'Conectar cuenta de pagos'}
              </Button>
            </div>
          ) : connectStatus.charges_enabled ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-emerald-400">
                <CheckCircle className="w-4 h-4" />
                Tu cuenta está activa y puede recibir pagos protegidos.
              </div>
              <Button
                variant="outline"
                className="border-white/10 text-zinc-400"
                onClick={handleDashboard}
                data-testid="stripe-dashboard-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Ver panel de Stripe
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                Tu cuenta está pendiente de verificación. Completa el proceso en Stripe.
              </div>
              <Button
                onClick={handleOnboard}
                disabled={onboarding}
                className="bg-amber-500 hover:bg-amber-400 text-black"
              >
                Completar verificación
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Sales (Seller) */}
      {isSeller && sales.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-medium text-zinc-100">Mis ventas protegidas</h2>
          <div className="space-y-3">
            {sales.map(tx => {
              const st = STATUS_LABELS[tx.status] || STATUS_LABELS.pending;
              return (
                <div key={tx.id} className="glass-card p-4 flex items-center justify-between" data-testid={`sale-${tx.id}`}>
                  <div className="space-y-1">
                    <p className="text-sm text-zinc-200 font-medium">{tx.title}</p>
                    <div className="flex items-center gap-3 text-xs text-zinc-500">
                      <span>Comprador: {tx.buyer_name}</span>
                      <span>{formatDate(tx.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <span className="text-sm font-semibold text-zinc-200">{formatPrice(tx.seller_amount)}</span>
                      <span className={`block text-[10px] px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                    </div>
                    {tx.status === 'paid' && (
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white text-xs"
                        onClick={() => handleShip(tx.id)} data-testid={`ship-btn-${tx.id}`}>
                        Marcar envío
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Purchases (Buyer) */}
      {purchases.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-medium text-zinc-100">Mis compras protegidas</h2>
          <div className="space-y-3">
            {purchases.map(tx => {
              const st = STATUS_LABELS[tx.status] || STATUS_LABELS.pending;
              return (
                <div key={tx.id} className="glass-card p-4 flex items-center justify-between" data-testid={`purchase-${tx.id}`}>
                  <div className="space-y-1">
                    <p className="text-sm text-zinc-200 font-medium">{tx.title}</p>
                    <div className="flex items-center gap-3 text-xs text-zinc-500">
                      <span>{formatDate(tx.created_at)}</span>
                      {tx.auto_release_at && tx.status === 'shipped' && (
                        <span className="text-amber-400">Auto-libera: {formatDate(tx.auto_release_at)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <span className="text-sm font-semibold text-zinc-200">{formatPrice(tx.amount)}</span>
                      <span className={`block text-[10px] px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                    </div>
                    {tx.status === 'shipped' && (
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
                        onClick={() => handleConfirmDelivery(tx.id)} data-testid={`confirm-delivery-btn-${tx.id}`}>
                        Confirmar recepción
                      </Button>
                    )}
                    {['paid', 'shipped', 'delivered', 'released', 'auto_released'].includes(tx.status) && (
                      <WhatsAppPostPurchaseButton orderType="protected_purchase" orderId={tx.id} />
                    )}
                    {['paid', 'shipped'].includes(tx.status) && (
                      <Button size="sm" variant="outline" className="border-red-500/20 text-red-400 text-xs"
                        onClick={() => { /* dispatch dispute modal */ }}
                        data-testid={`dispute-btn-${tx.id}`}>
                        Disputar
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isSeller && purchases.length === 0 && (
        <div className="text-center py-16">
          <Shield className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h3 className="text-lg text-zinc-400 mb-2">Sin compras protegidas</h3>
          <p className="text-sm text-zinc-600">Tus transacciones con Compra Protegida aparecerán aquí.</p>
        </div>
      )}
    </div>
  );
}
